import { forwardRef, useEffect, useRef, useState } from 'react';
import { produce } from 'immer';
import DaumPostcodeEmbed from 'react-daum-postcode';
import { useParams } from 'react-router-dom';

import { useBoundStore } from 'app/store';
import { FormInput } from 'features/HsptlzForm/ui';
import { withNamespace } from 'shared/lib';
import { lazyFn } from 'shared/lib/utils';

import { RELATIONSHIP } from '../config';
import { useCaregiverFormContext } from '../model';

import { AccompanimentToggle } from './AccompanimentToggle';

const Field = ({ children }) => {
  const { contentCode } = useParams();
  const processHsptlzFormData = useBoundStore((state) => state['hsptlzForm'].processFormData);
  const { caregiverRequired } = processHsptlzFormData[contentCode];
  const { accompaniment, setAccompaniment } = useCaregiverFormContext();

  return (
    <>
      <AccompanimentToggle accompaniment={accompaniment} setAccompaniment={setAccompaniment} />
      {/* 보호자 동행 */}
      {accompaniment === 'Y' && children}
      {/* 보호자 동행 아님 */}
      {accompaniment === 'N' && (
        <>
          <div className='caregiver-warning-box'>
            <span>
              입원 수속 시 보호자 정보와 서명 입력이 필요해요.
              <br />
              보호자분과 동행하지 않으셨다면, 입원 수속 창구에 방문하셔서 수속을 이어서 진행할 수 있어요
            </span>
          </div>
          {caregiverRequired === 'N' && children}
        </>
      )}
    </>
  );
};

const InfoField = ({ children }) => {
  return (
    <div className='identification-form caregiver-info'>
      <div className='certi-items'>
        <div className=''>
          <h3>보호자 정보</h3>
          <div className='input-wrap'>{children}</div>
        </div>
      </div>
    </div>
  );
};

const Relationship = forwardRef(({ relationship, onSelectRelationship }, relationshipSelectRef) => {
  return (
    <div
      className='item item-relationship'
      onClick={(e) => {
        relationshipSelectRef.current.classList.toggle('active');

        if (relationshipSelectRef.current && relationshipSelectRef.current.classList.contains('active')) {
            const TARGET_BOTTOM_GAP = 70 + 54;
            const TARGET = relationshipSelectRef.current.querySelector('ul.options');
            const rect = TARGET.getBoundingClientRect();
      
            const viewportHeight = window.innerHeight;
      
            const NOW_BOTTOM_GAP = viewportHeight - rect.bottom;

      
            if (NOW_BOTTOM_GAP < TARGET_BOTTOM_GAP) {
              window.scrollTo({
                top: window.scrollY + TARGET_BOTTOM_GAP - NOW_BOTTOM_GAP,
                left: 0,
                behavior: 'smooth',
              });
          }
        }
        e.stopPropagation();
      }}
    >
      <div className='select-form' ref={relationshipSelectRef}>
        <FormInput
          type='text'
          name='telecomText'
          id='telecomText'
          placeholder='환자와의 관계'
          readOnly
          className='inp-text-01'
          value={relationship}
        />
        <ul className='options'>
          {RELATIONSHIP.map((_relationship) => (
            <li
              className={relationship === _relationship ? 'selected' : ''}
              key={_relationship}
              onMouseDown={(e) => {
                onSelectRelationship(e.target.textContent);
              }}
            >
              <button type='button'>{_relationship}</button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
});

Relationship.displayName = 'Relationship';

const Name = ({ nameInputRef }) => {
  const { setInvalidFields } = useCaregiverFormContext();

  const onBlurName = () => {
    setInvalidFields((prev) => ({ ...prev }));
  };

  return (
    <div className='item item-name'>
      <FormInput
        type='text'
        placeholder='보호자 이름을 입력해주세요'
        name='userNm'
        className='inp-text-01 input-name'
        onBlur={onBlurName}
        ref={nameInputRef}
        maxLength={30}
      />
    </div>
  );
};

// 레이아웃 구조에 따라 FormInput 외부에서 error 처리
const BirthAndGender = ({ birthInputRef, sexInputRef, onBlurBirth }) => {
  const { invalidFields } = useCaregiverFormContext();
  const firstSubmitForm = useBoundStore((state) => state.hsptlzForm.firstSubmitForm);

  const [isInvalid, setIsInvalid] = useState(false);
  const [invalidText, setInvalidText] = useState('');

  useEffect(() => {
    const invalid = invalidFields.birth && !firstSubmitForm;
    setIsInvalid(invalid);
    setInvalidText(
      invalid
        ? birthInputRef.current.value.length < 8
          ? '생년월일은 8자리 숫자로 입력해 주세요.'
          : '생년월일이 정확한지 확인해 주세요.'
        : '',
    );
  }, [invalidFields.birth, firstSubmitForm]);

  return (
    <>
      <div className='item-birth'>
        <div className='item-birth-row'>
          <FormInput
            type='text'
            pattern='\d*'
            placeholder='생년월일 8자리'
            name='birthDtm'
            className={`inp-text-01 input-name ${isInvalid ? 'invalid' : ''}`}
            inputMode='numeric'
            onBlur={onBlurBirth}
            maxLength={8}
            ref={birthInputRef}
          />
          <div className='new-check-group'>
            <FormInput type='checkbox' id='toggle-chk-gender' className='toggle-checkbox' ref={sexInputRef} />
            <label htmlFor='toggle-chk-gender' className='toggle-container'>
              <div>
                <span>남</span>
              </div>
              <div>
                <span>여</span>
              </div>
            </label>
          </div>
        </div>
        {isInvalid && <div className='invalid-text'>{invalidText}</div>}
      </div>
    </>
  );
};

const TelContainer = forwardRef(({ phoneInputDataRef, msgRcvTelMaxNum }, ref) => {
  const { invalidFields, setInvalidFields } = useCaregiverFormContext();
  const [telList, setTelList] = useState(['']);
  const isRemove = useRef(false);

  useEffect(() => {
    if (isRemove.current) {
      isRemove.current = false;

      telList.forEach((tel, index) => {
        if (tel.length >= 10 && tel.length <= 11 && tel.at(0) === '0') {
          setInvalidFields(
            produce((draft) => {
              draft.tel[index] = false;
            }),
          );
        } else
          setInvalidFields(
            produce((draft) => {
              draft.tel[index] = true;
            }),
          );
      });
    }

    phoneInputDataRef.current = telList.join('|');
  }, [telList]);

  const onBlurTel = (e, index) => {
    setTelList(
      produce((draft) => {
        draft[index] = e.target.value;
      }),
    );

    if (e.target.value.length >= 10 && e.target.value.length <= 11 && e.target.value.at(0) === '0') {
      const newTel = invalidFields.tel.map((tel, i) => (i === index ? false : tel));
      setInvalidFields({ ...invalidFields, tel: newTel });
    } else {
      const newTel = invalidFields.tel.map((tel, i) => (i === index ? true : tel));
      setInvalidFields({ ...invalidFields, tel: newTel });
    }
  };

  const addTel = () => {
    setTelList([...telList, '']);
    setInvalidFields(
      produce((draft) => {
        draft.tel.push(undefined);
      }),
    );
  };

  const onRemoveTel = (index) => {
    setInvalidFields(
      produce((draft) => {
        draft.tel.splice(index, 1);
      }),
    );
    setTelList(
      produce((draft) => {
        draft.splice(index, 1);
      }),
    );
  };

  return (
    <div className='info-item'>
      <h3 className='title'>보호자 연락처</h3>
      <div className='item item-tel contact-input-container'>
        <div className='contact-input-wrap'>
          {telList.map((value, index) => (
            <Tel
              key={index + value}
              index={index}
              value={value}
              invalid={invalidFields.tel[index]}
              onBlurTel={onBlurTel}
              onRemoveTel={onRemoveTel}
              isMultiple={msgRcvTelMaxNum > 1}
              canRemove={telList.length > 1}
              isRemove={isRemove}
              ref={(el) => (ref.current[index] = el)}
            />
          ))}
        </div>
        {telList.length < Number(msgRcvTelMaxNum) && (
          <div className='contact-input-add'>
            <button type='button' onMouseDown={addTel} className='contact-input-add-btn'>
              <span>연락처 추가</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
});
TelContainer.displayName = 'TelContainer';

const Tel = forwardRef(({ index, value, invalid, isMultiple, canRemove, isRemove, onBlurTel, onRemoveTel }, ref) => {
  const [tel, setTel] = useState(value);

  return (
    <div className='contact-input-item'>
      {isMultiple && <div className='contact-input-label'>연락처 {index + 1}</div>}
      <div className='contact-input-field'>
        <FormInput
          type='text'
          placeholder='예) 01012345678'
          name='tel'
          className={`inp-text-01 input-name`}
          inputMode='numeric'
          minLength={10}
          maxLength={11}
          value={tel}
          invalid={invalid}
          onChange={(e) => setTel(e.target.value)}
          onBlur={(e) => onBlurTel(e, index)}
          ref={ref}
        />
      </div>
      {canRemove && (
        <button
          className='contact-remove-btn'
          onMouseDown={() => {
            isRemove.current = true;
            lazyFn(onRemoveTel, index);
          }}
        >
          <span>삭제</span>
        </button>
      )}
    </div>
  );
});
Tel.displayName = 'Tel';

const AddressContainer = () => {
  const { setAddressData } = useCaregiverFormContext();
  const [showPostcode, setShowPostcode] = useState(false);
  const addrSectionRef = useRef(null);
  const postcodeRef = useRef(null);

  useEffect(() => {
    if (!showPostcode) return;

    function handleClickOutside(e) {
      if (postcodeRef.current && !postcodeRef.current.contains(e.target)) {
        setShowPostcode(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPostcode]);

  const onClickAddress = () => {
    setShowPostcode(true);
    setTimeout(() => {
      if (addrSectionRef.current) {
        const rect = addrSectionRef.current.getBoundingClientRect();
        window.scrollTo({
          top: window.scrollY + rect.top - 100,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const onChangeAddress = (e) => {
    setAddressData((prev) => ({ ...prev, dtlAddr: e.target.value }));
  };

  return (
    <div className='caregiver-address'>
      <h3>보호자 주소</h3>
      <div className='address-wrap'>
        <BasicAddress addrSectionRef={addrSectionRef} onClickAddress={onClickAddress} />
        <DaumPostcode showPostcode={showPostcode} setShowPostcode={setShowPostcode} postcodeRef={postcodeRef} />
        <DetailAddress onChangeAddress={onChangeAddress} />
      </div>
    </div>
  );
};

const BasicAddress = ({ addrSectionRef, onClickAddress }) => {
  const { addressData } = useCaregiverFormContext();
  return (
    <div className='item item-address' ref={addrSectionRef}>
      <FormInput
        type='text'
        placeholder='주소를 검색해 주세요'
        name='userNm'
        className='inp-text-01 input-name'
        value={addressData.bscAddr && addressData.bscAddr !== 'null' ? addressData.bscAddr : ''}
        readOnly
        onClick={onClickAddress}
      />
    </div>
  );
};

const DetailAddress = ({ onChangeAddress }) => {
  const { addressData } = useCaregiverFormContext();
  const dtlAddrInputRef = useRef(null);
  return (
    <FormInput
      type='text'
      placeholder='상세주소를 입력해 주세요'
      name='userNm'
      className='inp-text-01 input-name'
      value={addressData.dtlAddr && addressData.dtlAddr !== 'null' ? addressData.dtlAddr : ''}
      onChange={onChangeAddress}
      ref={dtlAddrInputRef}
    />
  );
};

export const DaumPostcode = ({ showPostcode, setShowPostcode, postcodeRef }) => {
  const { addressData, setAddressData } = useCaregiverFormContext();
  return (
    showPostcode && (
      <div key={showPostcode} className='icon-close-wrap' ref={postcodeRef}>
        <button className='icon-close' onClick={() => setShowPostcode(false)}>
          닫기
        </button>

        <DaumPostcodeEmbed
          onComplete={({ address, buildingName, zonecode }) => {
            setAddressData({
              ...addressData,
              postNo: zonecode,
              bscAddr: `${address} ${buildingName}`.trimEnd(),
              dtlAddr: '',
            });
            setShowPostcode(false);
          }}
          submitMode={false}
        />
      </div>
    )
  );
};

export default withNamespace(Field, {
  InfoField,
  Relationship,
  Name,
  BirthAndGender,
  TelContainer,
  AddressContainer,
});

